import { useState } from 'react';
import { Link, NavLink, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { api, BASE } from '../lib/api';
import { dNum, hm } from '../lib/domain';
import { useMe } from '../hooks';
import { DateField, PersonPicker } from '../fields';
import { ErrorBox, Icon, Loading, PageHead } from '../ui';

/** Tabs of the admin area (Feedback, Aktionen, Debug-Log) */
export function AdminTabs() {
  return (
    <nav className="seg admin-tabs" aria-label="Admin-Bereich">
      <NavLink to="/admin/feedback">Feedback</NavLink>
      <NavLink to="/admin/aktionen">Aktionen</NavLink>
      <NavLink to="/admin/debug">Debug-Log</NavLink>
    </nav>
  );
}

/** Shown instead of an admin page to members */
export function AdminOnly() {
  return (
    <>
      <PageHead title="Nur für Admins" />
      <p>
        Zurück zu den <Link to="/">Terminen</Link>.
      </p>
    </>
  );
}

interface AuditItem {
  id: number;
  at: string;
  actor: { id: number; name: string } | null;
  as_admin: boolean;
  action: string;
  action_label: string;
  target: { type: string; id: number | null; label: string | null } | null;
  member: { id: number; name: string } | null;
  changes: string[];
  note: string | null;
}
const FILTERS = ['member', 'action', 'from', 'to', 'admin'] as const;
const at = (iso: string) => {
  const d = new Date(iso);
  return `${dNum(d)} ${hm(d)}`;
};
const targetLink = (t: AuditItem['target']) =>
  !t?.id
    ? null
    : t.type === 'event'
      ? `/termine/${t.id}`
      : t.type === 'article'
        ? `/flohmarkt/${t.id}`
        : t.type === 'member'
          ? `/mitglieder/${t.id}`
          : t.type === 'post'
            ? '/mitteilungen'
            : t.type === 'feedback'
              ? '/admin/feedback'
              : null;

/** Actions log: who did what and when – filter by member, action, period and admin actions; CSV download */
export function AuditLog() {
  const { data: me } = useMe();
  const isAdmin = me?.role === 'admin';
  const [params, setParams] = useSearchParams();
  // on phones the filter is folded (it would fill the whole screen) unless one is active
  const [showFilter, setShowFilter] = useState(
    () => params.toString() !== '' || !window.matchMedia('(max-width: 600px)').matches,
  );
  const filter = Object.fromEntries(FILTERS.flatMap(k => (params.get(k) ? [[k, params.get(k)!]] : [])));
  const query = new URLSearchParams(filter).toString();
  const set = (k: (typeof FILTERS)[number], v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };
  const members = useQuery({
    queryKey: ['audit', 'members'],
    // all members incl. former ones – their actions are often the ones to look up
    queryFn: () => api<{ id: number; first_name: string; last_name: string; status: string }[]>('/admin/audit/members'),
    enabled: isAdmin,
  });
  const actions = useQuery({
    queryKey: ['audit', 'actions'],
    queryFn: () => api<{ key: string; label: string }[]>('/admin/audit/actions'),
    enabled: isAdmin,
  });
  const log = useInfiniteQuery({
    queryKey: ['audit', query],
    queryFn: ({ pageParam }) =>
      api<{ items: AuditItem[]; next: number | null }>(
        `/admin/audit?${query}${pageParam ? `${query ? '&' : ''}before=${pageParam}` : ''}`,
      ),
    initialPageParam: 0,
    getNextPageParam: last => last.next ?? undefined,
    enabled: isAdmin,
  });
  if (me && !isAdmin) return <AdminOnly />;
  const items = log.data?.pages.flatMap(p => p.items) ?? [];

  return (
    <>
      <PageHead
        title="Aktionen"
        right={
          <a className="btn btn-ghost" href={`${BASE}/api/admin/audit.csv${query ? `?${query}` : ''}`} download>
            <Icon name="down" sw={1.8} />
            CSV
          </a>
        }
      />
      <AdminTabs />
      <p className="note">
        Wer hat wann was getan – alle Mitglieder. „Admin“ markiert Aktionen, die nur Admins dürfen (z. B. Termine,
        Anzeigen oder Mitteilungen anderer ändern, Mitglieder anlegen, anheften). Was ein Admin wie jedes Mitglied tut –
        eigene Termine, Zusagen, Kommentare –, ist keine Admin-Aktion. Einträge werden nach 12 Monaten gelöscht. „CSV“
        lädt die gefilterte Liste für eine Tabellenkalkulation herunter.
      </p>
      <details
        className="audit-filter-box"
        open={showFilter}
        onToggle={e => setShowFilter((e.target as HTMLDetailsElement).open)}
      >
        <summary>Filter{query ? ' (aktiv)' : ''}</summary>
        <div className="form panel audit-filter">
          <PersonPicker
            label="Mitglied"
            placeholder="Alle – Name eintippen …"
            people={(members.data ?? []).map(m => ({ ...m, note: m.status !== 'active' ? '(ehemalig)' : undefined }))}
            value={filter.member ? Number(filter.member) : null}
            onChange={id => set('member', id ? String(id) : '')}
          />
          <label className="fl">
            <span>Aktion</span>
            <select value={filter.action ?? ''} onChange={e => set('action', e.target.value)}>
              <option value="">Alle</option>
              {actions.data?.map(a => (
                <option key={a.key} value={a.key}>
                  {a.label}
                </option>
              ))}
            </select>
          </label>
          <DateField label="Von" value={filter.from ?? ''} onChange={v => set('from', v)} />
          <DateField label="Bis" value={filter.to ?? ''} onChange={v => set('to', v)} />
          <label className="set-row audit-admin">
            <div>
              <b>Nur Admin-Aktionen</b>
              <p>was nur Admins dürfen</p>
            </div>
            <span className="switch">
              <input
                type="checkbox"
                checked={!!filter.admin}
                onChange={e => set('admin', e.target.checked ? '1' : '')}
                aria-label="Nur Admin-Aktionen"
              />
              <span />
            </span>
          </label>
          {query && (
            <button type="button" className="btn btn-ghost" onClick={() => setParams({}, { replace: true })}>
              Filter zurücksetzen
            </button>
          )}
        </div>
      </details>
      {log.isLoading ? (
        <Loading />
      ) : log.error ? (
        <ErrorBox error={log.error} />
      ) : !items.length ? (
        <div className="empty">
          <b>Keine Einträge.</b>
        </div>
      ) : (
        <ol className="audit-list">
          {items.map(i => {
            const link = targetLink(i.target);
            return (
              <li key={i.id} className="audit-item">
                <div className="audit-head">
                  <time dateTime={i.at}>{at(i.at)}</time>
                  <b>{i.action_label}</b>
                  {i.as_admin && (
                    <span className="badge audit-badge" title="Admin-Aktion: nur mit Admin-Rechten möglich">
                      Admin
                    </span>
                  )}
                </div>
                <div className="audit-who">
                  {i.actor ? <Link to={`/mitglieder/${i.actor.id}`}>{i.actor.name}</Link> : <span>—</span>}
                  {i.member && i.member.id !== i.actor?.id && (
                    <>
                      {' · betrifft '}
                      <Link to={`/mitglieder/${i.member.id}`}>{i.member.name}</Link>
                    </>
                  )}
                  {i.target?.label && i.target.type !== 'member' && (
                    <>
                      {' · '}
                      {link ? <Link to={link}>{i.target.label}</Link> : i.target.label}
                    </>
                  )}
                </div>
                {i.note && <p className="audit-note">{i.note}</p>}
                {i.changes.length > 0 && (
                  <ul className="audit-changes">
                    {i.changes.map((c, n) => (
                      <li key={n}>{c}</li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ol>
      )}
      {log.hasNextPage && (
        <div className="form-actions">
          <button className="btn btn-ghost" disabled={log.isFetchingNextPage} onClick={() => void log.fetchNextPage()}>
            {log.isFetchingNextPage ? 'Lädt …' : 'Ältere laden'}
          </button>
        </div>
      )}
    </>
  );
}

/** Debug log: technical events (requests, errors, mails, start, import) to download and analyze */
export function DebugLog() {
  const { data: me } = useMe();
  const isAdmin = me?.role === 'admin';
  const summary = useQuery({
    queryKey: ['debug', 'summary'],
    queryFn: () => api<{ total: number; warn: number; error: number }>('/admin/debug-log/summary'),
    enabled: isAdmin,
  });
  if (me && !isAdmin) return <AdminOnly />;
  return (
    <>
      <PageHead title="Debug-Log" />
      <AdminTabs />
      <p className="note">
        Technische Einträge: Anfragen an den Server, Fehler, Mailversand, Start und Datenübernahme. Mitglieder stehen
        darin nur als Nummer, E-Mail-Adressen sind unkenntlich gemacht – die Datei kann zur Fehlersuche weitergegeben
        werden. Einträge werden nach 30 Tagen gelöscht.
      </p>
      {summary.data && (
        <p>
          Letzte 7 Tage: <b>{summary.data.total}</b> Einträge, davon <b>{summary.data.warn}</b> Warnungen und{' '}
          <b>{summary.data.error}</b> Fehler.
        </p>
      )}
      <div className="form-actions" style={{ justifyContent: 'flex-start' }}>
        {[1, 7, 30].map(days => (
          <a key={days} className="btn btn-ghost" href={`${BASE}/api/admin/debug-log?days=${days}`} download>
            <Icon name="down" sw={1.8} />
            {days === 1 ? 'Letzter Tag' : `Letzte ${days} Tage`}
          </a>
        ))}
      </div>
    </>
  );
}
